import { useState } from 'react'
import styled from 'styled-components'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../shared/StyledComponents'

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.cardBg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 639px) {
    flex-wrap: wrap;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  }
`

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`

const LogoText = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  margin: 0;
`

// With "Imputación" the bar no longer fits one row on a laptop: below 1366 px
// the items shrink and wrap; below 640 px they fold behind "☰ Menú".
const Nav = styled.nav<{ $open: boolean }>`
  display: flex;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;

  @media (max-width: 639px) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    flex-direction: column;
    order: 3;
    width: 100%;
  }
`

const MenuToggle = styled.button`
  display: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  cursor: pointer;

  @media (max-width: 639px) {
    display: inline-block;
  }
`

// Items take their natural width: a fixed 100 px minimum put the admin bar
// (eleven items) on two rows even on a 1500 px screen.
const NavItem = styled(NavLink)`
  padding: 0.75rem 0.65rem;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.normal};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};

  &:last-child {
    border-right: none;
  }

  @media (max-width: 1365px) {
    padding: 0.75rem 0.55rem;
  }

  @media (max-width: 639px) {
    text-align: left;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  &.active {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }

  &:not(.active):hover {
    background: rgba(0, 62, 87, 0.1);
  }
`

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
`

const UserInfo = styled.div`
  text-align: right;
`

const UserName = styled.div`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const UserDept = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const LogoutButton = styled(Button)`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};

  &:hover {
    background: ${({ theme }) => theme.colors.danger};
    color: white;
  }
`

export const Header: React.FC = () => {
    const { user, logout, needsProfileSetup } = useAuth()
    const navigate = useNavigate()
    const [menuOpen, setMenuOpen] = useState(false)
    // On a phone the stacked menu closes once a page is picked.
    const closeMenu = () => setMenuOpen(false)

    const handleLogout = () => {
        logout()
        navigate('/login')
    }

    const isAdmin = user?.role?.toLowerCase() === 'admin'
    const isDirector = isAdmin && user?.job_role === 'Director'

    return (
        <>
            <HeaderContainer>
                <Logo>
                    <LogoIcon />
                    <LogoText>Manager InputKnowledge</LogoText>
                </Logo>

                <MenuToggle
                    type="button"
                    aria-expanded={menuOpen}
                    aria-controls="main-nav"
                    onClick={() => setMenuOpen((v) => !v)}
                >
                    ☰ Menú
                </MenuToggle>

                {needsProfileSetup ? (
                    <Nav id="main-nav" $open={menuOpen} onClick={closeMenu}>
                        <NavItem to="/profile">Mi Perfil</NavItem>
                    </Nav>
                ) : (
                    <Nav id="main-nav" $open={menuOpen} onClick={closeMenu}>
                        {isAdmin && <NavItem to="/">Inicio</NavItem>}
                        {isAdmin && <NavItem to="/oportunidades">Oportunidades</NavItem>}
                        <NavItem to="/matriz-oportunidades">Matriz Oport.</NavItem>
                        <NavItem to="/matriz-conocimientos">Matriz Conoc.</NavItem>
                        <NavItem to="/timeline">Timeline</NavItem>
                        <NavItem to="/imputacion">Imputación</NavItem>
                        {(isAdmin || user?.can_view_infra) && <NavItem to="/infraestructura">Infraestructura</NavItem>}
                        <NavItem to="/sota">SOTA</NavItem>
                        {isAdmin && <NavItem to="/estadisticas">Estadísticas</NavItem>}
                        {isAdmin && <NavItem to="/admin">⚙️</NavItem>}
                        {!isDirector && <NavItem to="/profile">Mi Perfil</NavItem>}
                    </Nav>
                )}

                <UserSection>
                    <UserInfo>
                        <UserName>{user?.display_name || 'Usuario'}</UserName>
                        <UserDept>{user?.department || 'General'}</UserDept>
                    </UserInfo>
                    <LogoutButton variant="ghost" size="sm" onClick={handleLogout}>
                        Cerrar Sesión
                    </LogoutButton>
                </UserSection>
            </HeaderContainer>
        </>
    )
}
